import React, { useState, useEffect } from 'react';
import { Offcanvas, OffcanvasBody, OffcanvasHeader, Input, Button, Label, Spinner } from 'reactstrap';
import CustomHeader from '../../../../../Components/Common/CustomHeader';
import apiClient from '../../../../../api/core/apiClient';

interface EditCustomerOffcanvasProps {
    isOpen: boolean;
    onClose: () => void;
    customer: any;
    onSuccess: (updatedCustomer: any) => void;
}

export const EditCustomerOffcanvas: React.FC<EditCustomerOffcanvasProps> = ({
    isOpen,
    onClose,
    customer,
    onSuccess
}) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [documentId, setDocumentId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && customer) {
            setFirstName(customer.firstName || '');
            setLastName(customer.lastName || '');
            setEmail(customer.email || '');
            setDocumentId(customer.document || '');
            setError(null);
        }
    }, [isOpen, customer]);

    const handleSave = async () => {
        if (!firstName.trim()) {
            setError('El nombre es obligatorio');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const data = {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                document: documentId.trim()
            };

            // Se usa el endpoint solicitado: PUT crm/customer/:id
            const response = await apiClient.put(`/crm/customers/${customer.id}`, data);

            onSuccess(response || { ...customer, ...data });
            onClose();
        } catch (err: any) {
            console.error('Error al actualizar cliente', err);
            setError(err?.response?.data?.message || 'Ocurrió un error al guardar');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" className="d-flex flex-column">
            <CustomHeader title="Editar Cliente" onClose={onClose} component={OffcanvasHeader} />
            <OffcanvasBody className="flex-grow-1">
                {error && <div className="alert alert-danger mb-3">{error}</div>}

                <div className="mb-3">
                    <Label htmlFor="firstName-input" className="form-label">Nombre<span className="text-danger">*</span></Label>
                    <Input
                        type="text"
                        id="firstName-input"
                        className="form-control"
                        placeholder="Ingrese el nombre"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={isLoading}
                    />
                </div>

                <div className="mb-3">
                    <Label htmlFor="lastName-input" className="form-label">Apellido</Label>
                    <Input
                        type="text"
                        id="lastName-input"
                        className="form-control"
                        placeholder="Ingrese el apellido"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={isLoading}
                    />
                </div>

                <div className="mb-3">
                    <Label htmlFor="email-input" className="form-label">Correo</Label>
                    <Input
                        type="email"
                        id="email-input"
                        className="form-control"
                        placeholder="Ingrese el correo"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading}
                    />
                </div>

                <div className="mb-3">
                    <Label htmlFor="document-input" className="form-label">Cédula / Documento</Label>
                    <Input
                        type="text"
                        id="document-input"
                        className="form-control"
                        placeholder="Ingrese la cédula"
                        value={documentId}
                        onChange={(e) => setDocumentId(e.target.value)}
                        disabled={isLoading}
                    />
                </div>
            </OffcanvasBody>
            <div className="p-3 border-top">
                <div className="d-flex gap-2">
                    <Button color="light" onClick={onClose} className="w-100" disabled={isLoading}>
                        Cancelar
                    </Button>
                    <Button color="primary" onClick={handleSave} className="w-100" disabled={isLoading}>
                        {isLoading ? <Spinner size="sm" /> : 'Guardar'}
                    </Button>
                </div>
            </div>
        </Offcanvas>
    );
};
